import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle, ArrowLeft, User, Clock } from 'lucide-react';
import { format } from 'date-fns';

// Helper function to get initials from name
const getInitials = (firstname, lastname) => {
  const first = firstname?.charAt(0)?.toUpperCase() || '';
  const last = lastname?.charAt(0)?.toUpperCase() || '';
  return first + last;
};

// Function to deduplicate group messages - keep only one outbound message per group
const deduplicateGroupMessages = (messages) => {
  const deduplicated = [];
  const outboundGroups = new Map(); // Track outbound messages by body + time window
  
  for (const message of messages) {
    if (message.direction === 'outbound') {
      // Create a key based on body and time window (within 1 minute)
      const timeWindow = Math.floor(new Date(message.sent_at).getTime() / 60000); // Round to minute
      const key = `${message.body}-${timeWindow}`;
      
      if (!outboundGroups.has(key)) {
        outboundGroups.set(key, message);
        deduplicated.push(message);
      }
      // Skip duplicate outbound messages
    } else {
      // Always include inbound messages
      deduplicated.push(message);
    }
  }
  
  return deduplicated.sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
};

// Function to enrich messages with member data for inbound messages without member_id
const enrichMessagesWithMemberData = async (messages, conversationId) => {
  const enrichedMessages = [...messages];
  
  // Find inbound messages without member data
  const messagesNeedingMemberData = enrichedMessages.filter(
    msg => msg.direction === 'inbound' && !msg.member && msg.from_number
  );
  
  console.log('Messages needing member data:', messagesNeedingMemberData);
  
  if (messagesNeedingMemberData.length === 0) {
    return enrichedMessages;
  }
  
  // Get unique phone numbers
  const phoneNumbers = [...new Set(messagesNeedingMemberData.map(msg => msg.from_number))];
  
  console.log('Phone numbers to lookup:', phoneNumbers);
  
  // Try to find members by phone number using multiple approaches
  let members = [];
  
  // Get the current user's organization ID
  const getCurrentUserOrganizationId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', user.id)
      .single();
    
    return profile?.organization_id;
  };
  
  let organizationId = await getCurrentUserOrganizationId();
  console.log('Organization ID from profile:', organizationId);
  
  if (!organizationId) {
    console.log('No organization ID found in user profile, trying to get from conversation...');
    
    // Try to get organization ID from the conversation
    const { data: conversation } = await supabase
      .from('sms_conversations')
      .select('organization_id')
      .eq('id', conversationId)
      .single();
    
    if (conversation?.organization_id) {
      organizationId = conversation.organization_id;
      console.log('Found organization ID from conversation:', organizationId);
    } else {
      console.log('No organization ID found, cannot lookup members');
      return enrichedMessages;
    }
  }
  
  // Try exact match first
  const { data: exactMembers } = await supabase
    .from('members')
    .select('id, firstname, lastname, image_url, phone')
    .eq('organization_id', organizationId)
    .in('phone', phoneNumbers);
  
  if (exactMembers && exactMembers.length > 0) {
    members = exactMembers;
  } else {
    // Try with different phone number formats
    const allPhoneFormats = new Set();
    
    // Generate all possible formats for each phone number
    phoneNumbers.forEach(phone => {
      console.log('Processing phone number:', phone);
      
      // Original format
      allPhoneFormats.add(phone);
      
      // Remove all non-digits
      const digitsOnly = phone.replace(/\D/g, '');
      allPhoneFormats.add(digitsOnly);
      
      // Add +1 prefix
      allPhoneFormats.add(`+1${digitsOnly}`);
      
      // Remove leading 1
      const withoutLeading1 = digitsOnly.replace(/^1/, '');
      allPhoneFormats.add(withoutLeading1);
      allPhoneFormats.add(`+1${withoutLeading1}`);
      
      // Add parentheses format
      if (digitsOnly.length === 10) {
        allPhoneFormats.add(`(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`);
      }
      
      // Add spaces format for +1 numbers
      if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        allPhoneFormats.add(`+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`);
      }
      
      // Add dash format (like 925-813-9893)
      if (digitsOnly.length === 10) {
        allPhoneFormats.add(`${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`);
      }
      
      // Add dash format for +1 numbers (like 1-925-813-9893)
      if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        allPhoneFormats.add(`${digitsOnly.slice(0, 1)}-${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`);
        // Also add the 10-digit version without the leading 1 (like 925-813-9893)
        allPhoneFormats.add(`${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`);
      }
    });
    
    console.log('All phone formats to search:', Array.from(allPhoneFormats));
    
    // Try to find members with any of these formats
    console.log('Searching for members with organization_id:', organizationId);
    console.log('Searching for phone formats:', Array.from(allPhoneFormats));
    
    const { data: formatMembers, error: formatError } = await supabase
      .from('members')
      .select('id, firstname, lastname, image_url, phone')
      .eq('organization_id', organizationId)
      .in('phone', Array.from(allPhoneFormats));
    
    console.log('Format members query error:', formatError);
    
    console.log('Format members found:', formatMembers);
    
    if (formatMembers) {
      members = formatMembers;
    }
    
    // If still no matches, let's see what phone numbers are actually in the database
    if (members.length === 0) {
      console.log('No members found with phone formats, checking all members...');
      console.log('Querying all members with organization_id:', organizationId);
      const { data: allMembers, error: membersError } = await supabase
        .from('members')
        .select('id, firstname, lastname, phone')
        .eq('organization_id', organizationId)
        .limit(10);
      console.log('Sample members from database:', allMembers);
      console.log('Members query error:', membersError);
      
      // Try without organization filter to see if RLS is blocking
      console.log('Trying to query members without organization filter...');
      const { data: allMembersNoFilter, error: noFilterError } = await supabase
        .from('members')
        .select('id, firstname, lastname, phone, organization_id')
        .limit(5);
      console.log('Members without filter:', allMembersNoFilter);
      console.log('No filter error:', noFilterError);
      
      // Also check if we can access the organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id, name')
        .limit(1);
      console.log('Organization data:', orgData);
      console.log('Organization query error:', orgError);
    }
  }
  
  console.log('Found members:', members);
  
  // Create a map of phone numbers to members
  const phoneToMemberMap = new Map();
  if (members) {
    members.forEach(member => {
      // Try different phone number formats for matching
      const phoneFormats = [
        member.phone,
        member.phone?.replace(/\D/g, ''), // Remove all non-digits
        member.phone?.replace(/^1/, ''), // Remove leading 1
        `+1${member.phone?.replace(/\D/g, '')}`, // Add +1 prefix
        `+1${member.phone?.replace(/^1/, '').replace(/\D/g, '')}` // Add +1 and remove leading 1
      ].filter(Boolean);
      
      phoneFormats.forEach(format => {
        phoneToMemberMap.set(format, member);
      });
    });
  }
  
  // Enrich messages with member data
  enrichedMessages.forEach(message => {
    if (message.direction === 'inbound' && !message.member && message.from_number) {
      const member = phoneToMemberMap.get(message.from_number);
      if (member) {
        message.member = member;
      }
    }
  });
  
  return enrichedMessages;
};

// Function to fix messages that don't have member_id by looking up the member
const fixMessagesWithoutMemberId = async (messages, conversationId) => {
  console.log('fixMessagesWithoutMemberId called with', messages.length, 'messages');
  const fixedMessages = [...messages];
  
  // Find messages without member data
  const messagesNeedingFix = fixedMessages.filter(
    msg => msg.direction === 'inbound' && !msg.member && msg.from_number
  );
  
  console.log('Messages needing fix:', messagesNeedingFix.length);
  console.log('All messages:', messages.map(msg => ({
    id: msg.id,
    direction: msg.direction,
    hasMember: !!msg.member,
    from_number: msg.from_number
  })));
  
  if (messagesNeedingFix.length === 0) {
    console.log('No messages need fixing, returning original messages');
    return fixedMessages;
  }
  
  // Get organization ID from conversation
  console.log('Getting organization ID from conversation:', conversationId);
  const { data: conversation } = await supabase
    .from('sms_conversations')
    .select('organization_id')
    .eq('id', conversationId)
    .single();
  
  console.log('Conversation data:', conversation);
  
  if (!conversation?.organization_id) {
    console.log('No organization ID found, returning original messages');
    return fixedMessages;
  }
  
  // Get unique phone numbers
  const phoneNumbers = [...new Set(messagesNeedingFix.map(msg => msg.from_number))];
  console.log('Phone numbers to lookup:', phoneNumbers);
  
  // Try to find members by phone number
  let members = [];
  console.log('Looking up members with organization_id:', conversation.organization_id);
  const { data: initialMembers } = await supabase
    .from('members')
    .select('id, firstname, lastname, image_url, phone')
    .eq('organization_id', conversation.organization_id)
    .in('phone', phoneNumbers);
  
  console.log('Initial members found:', initialMembers);
  
  if (initialMembers && initialMembers.length > 0) {
    members = initialMembers;
    console.log('Using initial members:', members);
  } else {
    console.log('No initial members found, trying different phone number formats...');
    // Try with different phone number formats
    const allPhoneFormats = new Set();
    
    phoneNumbers.forEach(phone => {
      const digitsOnly = phone.replace(/\D/g, '');
      console.log('Processing phone:', phone, 'digits only:', digitsOnly);
      
      // Original format
      allPhoneFormats.add(phone);
      
      // Remove all non-digits
      allPhoneFormats.add(digitsOnly);
      
      // Add +1 prefix
      allPhoneFormats.add(`+1${digitsOnly}`);
      
      // Remove leading 1
      const withoutLeading1 = digitsOnly.replace(/^1/, '');
      allPhoneFormats.add(withoutLeading1);
      allPhoneFormats.add(`+1${withoutLeading1}`);
      
      // Add dash format for 10-digit numbers
      if (digitsOnly.length === 10) {
        allPhoneFormats.add(`${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`);
      }
      
      // Add dash format for 11-digit numbers
      if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        allPhoneFormats.add(`${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`);
      }
    });
    
    console.log('All phone formats to search:', Array.from(allPhoneFormats));
    
    // Try to find members with any of these formats
    const { data: formatMembers } = await supabase
      .from('members')
      .select('id, firstname, lastname, image_url, phone')
      .eq('organization_id', conversation.organization_id)
      .in('phone', Array.from(allPhoneFormats));
    
    console.log('Format members found:', formatMembers);
    
    if (formatMembers && formatMembers.length > 0) {
      members = formatMembers;
    } else {
      // If still no members found, try to get all members and match by cleaning phone numbers
      console.log('No members found with phone formats, trying to match by cleaning phone numbers...');
      const { data: allMembers } = await supabase
        .from('members')
        .select('id, firstname, lastname, image_url, phone')
        .eq('organization_id', conversation.organization_id);
      
      console.log('All members in organization:', allMembers);
      
      if (allMembers && allMembers.length > 0) {
        // Create a map of cleaned phone numbers to members
        const phoneToMemberMap = new Map();
        
        allMembers.forEach(member => {
          if (member.phone) {
            // Clean the member's phone number
            const cleanMemberPhone = member.phone.replace(/[^\d()-]/g, '');
            const digitsOnly = cleanMemberPhone.replace(/\D/g, '');
            const withoutLeading1 = digitsOnly.replace(/^1/, '');
            
            // Add various formats of the member's phone
            phoneToMemberMap.set(member.phone, member);
            phoneToMemberMap.set(cleanMemberPhone, member);
            phoneToMemberMap.set(digitsOnly, member);
            phoneToMemberMap.set(withoutLeading1, member);
            phoneToMemberMap.set(`+1${digitsOnly}`, member);
            phoneToMemberMap.set(`+1${withoutLeading1}`, member);
            
            // Add dash format for 10-digit numbers
            if (digitsOnly.length === 10) {
              phoneToMemberMap.set(`${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`, member);
            }
            
            // Add dash format for 11-digit numbers
            if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
              phoneToMemberMap.set(`${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`, member);
            }
          }
        });
        
        console.log('Phone to member map:', phoneToMemberMap);
        
        // Find matching members
        const matchingMembers = [];
        phoneNumbers.forEach(phone => {
          const member = phoneToMemberMap.get(phone);
          if (member && !matchingMembers.find(m => m.id === member.id)) {
            matchingMembers.push(member);
            console.log(`Found member for phone ${phone}:`, member.firstname, member.lastname);
          }
        });
        
        if (matchingMembers.length > 0) {
          members = matchingMembers;
          console.log('Found members by cleaning phone numbers:', members);
        }
      }
    }
  }
  
  if (!members || members.length === 0) {
    console.log('No members found, returning original messages');
    return fixedMessages;
  }
  
  // Create a map of phone numbers to members
  const phoneToMemberMap = new Map();
  members.forEach(member => {
    console.log('Processing member:', member.firstname, member.lastname, 'phone:', member.phone);
    
    // Try different phone number formats for matching
    const phoneFormats = [
      member.phone,
      member.phone?.replace(/\D/g, ''), // Remove all non-digits
      member.phone?.replace(/^1/, ''), // Remove leading 1
      `+1${member.phone?.replace(/\D/g, '')}`, // Add +1 prefix
      `+1${member.phone?.replace(/^1/, '').replace(/\D/g, '')}` // Add +1 and remove leading 1
    ].filter(Boolean);
    
    console.log('Phone formats for member:', phoneFormats);
    
    phoneFormats.forEach(format => {
      phoneToMemberMap.set(format, member);
      console.log('Added to map:', format, '->', member.firstname);
    });
  });
  
  console.log('Phone to member map:', phoneToMemberMap);
  
  // Fix messages with member data
  for (const message of fixedMessages) {
    if (message.direction === 'inbound' && !message.member && message.from_number) {
      console.log('Looking up member for phone:', message.from_number);
      const member = phoneToMemberMap.get(message.from_number);
      if (member) {
        console.log('Found member:', member.firstname, member.lastname);
        message.member = member;
        
        // Update the message in the database to set the member_id
        try {
          await supabase
            .from('sms_messages')
            .update({ member_id: member.id })
            .eq('id', message.id);
          console.log('Updated message in database');
        } catch (error) {
          console.log('Could not update database:', error);
        }
      } else {
        console.log('No member found for phone:', message.from_number);
      }
    }
  }
  
  return fixedMessages;
};

const ConversationView = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (conversationId) {
      fetchConversation();
    }
  }, [conversationId]);

  const fetchConversation = async () => {
    try {
      setLoading(true);
      
      // Fetch conversation details
      const { data: conversationData, error: convError } = await supabase
        .from('sms_conversations')
        .select(`
          id,
          title,
          conversation_type,
          status,
          created_at,
          updated_at,
          group_id,
          groups (
            name
          )
        `)
        .eq('id', conversationId)
        .single();

      if (convError) {
        throw convError;
      }

      if (!conversationData) {
        throw new Error('Conversation not found');
      }

      setConversation(conversationData);

      // Fetch messages for this conversation
      const { data: messagesData, error: messagesError } = await supabase
        .from('sms_messages')
        .select(`
          id,
          direction,
          body,
          status,
          sent_at,
          from_number,
          to_number,
          member_id,
          member:members (
            id,
            firstname,
            lastname,
            image_url,
            phone
          )
        `)
        .eq('conversation_id', conversationId)
        .order('sent_at', { ascending: true });

      if (messagesError) {
        throw messagesError;
      }

      // Fix messages that don't have member_id by looking up the member
      console.log('Raw messages from database:', messagesData);
      const fixedMessages = await fixMessagesWithoutMemberId(messagesData || [], conversationId);
      console.log('Fixed messages after member lookup:', fixedMessages);
      
      // Deduplicate outbound messages that are part of the same group message
      const deduplicatedMessages = deduplicateGroupMessages(fixedMessages);
      setMessages(deduplicatedMessages);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSenderInfo = (message) => {
    if (message.direction === 'outbound') {
      return {
        name: 'Deacon App',
        type: 'outbound'
      };
    } else if (message.member) {
      return {
        name: `${message.member.firstname || ''} ${message.member.lastname || ''}`.trim() || 'Unknown Member',
        type: 'member',
        memberId: message.member.id,
        imageUrl: message.member.image_url
      };
    } else {
      return {
        name: 'Unknown Sender',
        type: 'unknown'
      };
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown';
    // Format phone number for display
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="text-red-500 mb-4">
              <MessageCircle className="h-12 w-12 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Conversation Not Found</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{conversation.title}</h1>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant="secondary">
                    {conversation.conversation_type || 'general'}
                  </Badge>
                  {conversation.groups && (
                    <Badge variant="outline">
                      {conversation.groups.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {format(new Date(conversation.updated_at), 'MMM d, yyyy h:mm a')}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              Messages ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No messages in this conversation yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const senderInfo = getSenderInfo(message);
                  const isOutbound = message.direction === 'outbound';
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          isOutbound
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          {senderInfo.imageUrl ? (
                            <Avatar className="h-4 w-4">
                              <AvatarImage src={senderInfo.imageUrl} alt={senderInfo.name} />
                              <AvatarFallback className="text-xs">
                                {message.member ? getInitials(message.member.firstname, message.member.lastname) : 'U'}
                              </AvatarFallback>
                            </Avatar>
                          ) : (
                            <User className="h-3 w-3" />
                          )}
                          <span className="text-xs font-medium">
                            {senderInfo.name}
                          </span>
                          <Clock className="h-3 w-3" />
                          <span className="text-xs opacity-75">
                            {format(new Date(message.sent_at), 'h:mm a')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">
                          {message.body}
                        </p>
                        {!isOutbound && (
                          <p className="text-xs opacity-75 mt-1">
                            {formatPhoneNumber(message.from_number)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Login Prompt */}
        <Card className="mt-6">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold mb-2">Want to participate in this conversation?</h3>
            <p className="text-gray-600 mb-4">
              Sign in to Deacon to send messages and manage conversations.
            </p>
           
            <Button onClick={() => navigate('/')} className="mr-2">
              Learn More
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConversationView;
